import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { MediaPlatform } from '@nativewaves/nw-cloud-sdk/media'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useMediaPlatformCreateMutation, useMediaPlatformDeleteMutation } from 'hooks/mutations/media'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<MediaPlatform>) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createMediaPlatform, ...mediaPlatformCreateMutation } = useMediaPlatformCreateMutation()
  const { mutate: deleteMediaPlatform } = useMediaPlatformDeleteMutation()

  const handleDuplicateMediaPlatform = useCallback(() => {
    createMediaPlatform(
      {
        mediaPlatformInsert: {
          ...row.original,
          defaultWorkspaceId: row.original.defaultWorkspaceId || '',
          name: `${row.original.name} (${t('duplicate')})`,
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createMediaPlatform, onClose, row.original, t])

  const handleDeleteMediaPlatform = useCallback(() => {
    onClose()

    deleteMediaPlatform(
      { platformId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:MediaPlatform.nameDelete', { name: row.original.name }), { variant: 'success' }),
      },
    )
  }, [deleteMediaPlatform, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  return (
    <>
      <MenuItem onClick={handleDuplicateMediaPlatform} disabled={mediaPlatformCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {mediaPlatformCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteMediaPlatform} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
